/**
 * Taxonomy guidance panel for learning outcome authoring.
 * Shows a taxonomy's levels highlighted for one framework level, their verbs,
 * writing structure guidance, and examples.
 * @module components/ui/GuidancePanel
 */

import { useState } from "react";

import type { GuidanceStrategy } from "@/taxonomy/strategy";
import type { TaxonomyLevel } from "@/taxonomy/types";
import { VAGUE_VERBS } from "@/taxonomy/vague-verbs";

import { Badge, type BadgeProps } from "./Badge";
import { CollectionView } from "./CollectionView";
import type { Column } from "./DataTable";
import { LO_EXAMPLES } from "./guidance-examples";
import { Icon } from "./Icon";
import { TabPanel, Tabs } from "./Tabs";
import { SuitabilityLegend, VerbBadge } from "./VerbSuitability";

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

/** Badge appearance for a level given the current framework alignment. */
function levelBadgeProps(
  levelId: string,
  primary: readonly string[],
  secondary: readonly string[],
): Omit<BadgeProps, "children"> {
  if (primary.includes(levelId)) {
    return { tone: "primary", variant: "soft", size: "sm" };
  }
  if (secondary.includes(levelId)) {
    return { tone: "primary", variant: "outline", size: "sm" };
  }
  return { tone: "neutral", variant: "outline", size: "sm" };
}

/** Row highlight class for the taxonomy table. */
function rowClass(levelId: string, primary: readonly string[], secondary: readonly string[]) {
  if (primary.includes(levelId)) {
    return "bg-primary/8";
  }
  if (secondary.includes(levelId)) {
    return "bg-secondary/5";
  }
  return "";
}

/** Highest rank first, so the most demanding level heads the table; unranked keep their order. */
function displayOrder(levels: readonly TaxonomyLevel[]): TaxonomyLevel[] {
  const ranked = levels.every((l) => l.rank !== null);
  return ranked ? [...levels].sort((a, b) => (b.rank ?? 0) - (a.rank ?? 0)) : [...levels];
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

/** What a caller passes to a strategy's `Panel`. */
export interface GuidancePanelProps {
  /** The programme's level in the strategy's framework. */
  level: number | null;
  contextLabel: string;
  /**
   * Whether it starts open. Read once, like every other panel here, so a
   * reader who folds it is not reopened by the next outcome they write.
   * Decide it from data that has already loaded.
   */
  defaultOpen?: boolean;
}

type GuidanceTab = "taxonomy" | "writing" | "examples";

/* ------------------------------------------------------------------ */
/*  Tab panels                                                         */
/* ------------------------------------------------------------------ */

interface TaxonomyTableProps {
  strategy: GuidanceStrategy;
  /** The framework level; 0 when unset. */
  level: number;
  primary: readonly string[];
  secondary: readonly string[];
}

function TaxonomyTable({ strategy, level, primary, secondary }: TaxonomyTableProps) {
  const { taxonomy, framework } = strategy;
  const where = `${framework.shortName} ${level}`;
  const rated = strategy.emphasisFor(level) !== null;
  const columns: readonly Column<TaxonomyLevel>[] = [
    {
      key: "level",
      header: "Level",
      width: "w-28",
      render: (tl) => (
        <span className="font-semibold whitespace-nowrap">
          <Badge {...levelBadgeProps(tl.id, primary, secondary)}>{tl.name}</Badge>
        </span>
      ),
    },
    {
      key: "description",
      header: "Description",
      width: "w-48",
      render: (tl) => <span className="text-xs text-muted">{tl.description}</span>,
    },
    {
      key: "verbs",
      header: "Action Verbs",
      render: (tl) => (
        <div className="flex flex-wrap gap-1">
          {tl.verbs.map((v) => {
            const suitability = strategy.suitability(v.lemma, level);
            return (
              <VerbBadge
                key={v.lemma}
                verb={v}
                suitability={suitability}
                where={where}
                levels={taxonomy.levels}
                primary={primary}
                secondary={secondary}
              />
            );
          })}
        </div>
      ),
    },
  ];
  return (
    <div data-testid={`${taxonomy.id}-taxonomy-panel`}>
      {rated && <SuitabilityLegend where={where} testId={`${taxonomy.id}-legend`} />}
      <CollectionView
        storageKey="taxonomy-guidance"
        caption={`${taxonomy.name}: levels and their action verbs`}
        columns={columns}
        rows={displayOrder(taxonomy.levels)}
        rowKey={(tl) => tl.id}
        density="sm"
        viewToggle={false}
        rowClassName={(tl) => rowClass(tl.id, primary, secondary)}
        emptyState="No levels to show."
      />
    </div>
  );
}

function WritingGuidePanel({ strategy }: { strategy: GuidanceStrategy }) {
  return (
    <div data-testid={`${strategy.taxonomy.id}-writing-panel`}>
      {/* Structure */}
      <h4 className="font-semibold mb-1">Learning Outcome Structure</h4>
      <p className="mb-2">
        Each outcome should follow the pattern: <strong>Action Verb</strong> +{" "}
        <strong>Object / Content</strong> + <strong>Context or Standard</strong>.
      </p>
      <div className="bg-base-200 rounded-lg p-2 mb-3 font-mono text-xs">
        <span className="text-primary font-bold">Evaluate</span>{" "}
        <span className="text-secondary">competing models of consumer behaviour</span>{" "}
        <span className="text-accent">using empirical evidence from peer-reviewed research</span>
      </div>

      {/* Checklist */}
      <h4 className="font-semibold mb-1">Quality Checklist</h4>
      <ul className="list-disc list-inside space-y-1 mb-3">
        <li>
          <strong>Observable:</strong> Could an assessor see or measure this? Avoid
          &quot;understand&quot;, &quot;know&quot;, &quot;be familiar with&quot;.
        </li>
        <li>
          <strong>Specific:</strong> Is the scope clear? Broad outcomes are hard to assess.
        </li>
        <li>
          <strong>One verb per outcome:</strong> Compound outcomes (e.g., &quot;analyse <em>and</em>{" "}
          evaluate&quot;) should generally be split.
        </li>
        <li>
          <strong>Appropriate level:</strong> Does the verb match the expected{" "}
          {strategy.taxonomy.shortName} / {strategy.framework.shortName} level?
        </li>
        <li>
          <strong>Assessable:</strong> Can you design an assessment that directly tests this
          outcome?
        </li>
      </ul>

      {/* Verbs to avoid */}
      <h4 className="font-semibold mb-1">Verbs to Avoid</h4>
      <p className="mb-1">
        These are commonly flagged because they describe internal states rather than observable
        actions:
      </p>
      <div className="flex flex-wrap gap-1 mb-2">
        {VAGUE_VERBS.map((v) => (
          <Badge key={v.id} tone="error" variant="outline" size="xs">
            {v.phrase}
          </Badge>
        ))}
      </div>
      <p className="text-muted text-xs">
        Replace these with an action verb from the taxonomy table that matches the cognitive level
        you intend.
      </p>
    </div>
  );
}

function ExamplesPanel({ testId }: { testId: string }) {
  return (
    <div data-testid={testId}>
      <p className="mb-2">Compare well-written outcomes with common pitfalls:</p>
      <div className="space-y-3">
        {LO_EXAMPLES.map((ex) => (
          <div key={ex.poor} className="bg-base-200 rounded-lg p-3">
            <div className="flex items-start gap-2 mb-1">
              <Icon name="x-circle" className="text-error-strong mt-0.5" aria-hidden />
              <div>
                <span className="line-through text-muted">{ex.poor}</span>
                <span className="text-xs text-error-strong ml-2">— {ex.issue}</span>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Icon name="check-circle" className="text-success-strong mt-0.5" aria-hidden />
              <span>{ex.good}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Guidance panel for one taxonomy against one framework.
 * Shows every level highlighted for the framework level, verb lists,
 * outcome writing structure, and good / poor examples.
 */
export function GuidancePanel({
  strategy,
  level,
  contextLabel,
  defaultOpen = true,
}: GuidancePanelProps & { strategy: GuidanceStrategy }) {
  const [activeTab, setActiveTab] = useState<GuidanceTab>("taxonomy");
  const { taxonomy, framework } = strategy;
  const prefix = taxonomy.id;
  const lvl = Number(level ?? 0);

  const title = lvl
    ? `${taxonomy.name} Helper — ${framework.shortName} Level ${lvl}`
    : `${taxonomy.name} Helper`;

  const emphasis = strategy.emphasisFor(lvl);
  const focusText =
    emphasis?.summary ??
    `Select the programme ${framework.shortName} level in the Identity step to see tailored guidance.`;

  const tabs = [
    {
      key: "taxonomy",
      label: "Verb Taxonomy",
      icon: "table",
      "data-testid": `${prefix}-tab-taxonomy`,
    },
    {
      key: "writing",
      label: "Writing Guide",
      icon: "pencil-line",
      "data-testid": `${prefix}-tab-writing`,
    },
    {
      key: "examples",
      label: "Examples",
      icon: "check-circle",
      "data-testid": `${prefix}-tab-examples`,
    },
  ];

  return (
    <div
      className="collapse collapse-arrow bg-info/10 border border-info/20 rounded-lg mb-3"
      data-testid={`${prefix}-guidance`}
    >
      <input
        type="checkbox"
        defaultChecked={defaultOpen}
        aria-label={`Toggle ${taxonomy.shortName} guidance`}
      />

      {/* Header */}
      <div className="collapse-title text-sm font-semibold flex items-center gap-2">
        <Icon name="lightbulb" aria-hidden />
        {title} — for {contextLabel}
      </div>

      {/* Body */}
      <div className="collapse-content text-sm">
        <div className="mb-3" data-testid={`${prefix}-focus`}>
          {focusText}
        </div>

        {emphasis && (
          <div className="mb-3" data-testid={`${prefix}-suggested-verbs`}>
            <span className="font-semibold">Suggested action verbs for this level: </span>
            {strategy
              .suggest(lvl)
              .map((v) => v.lemma)
              .join(", ")}
          </div>
        )}

        <Tabs
          variant="box"
          items={tabs}
          value={activeTab}
          onChange={(key) => setActiveTab(key as GuidanceTab)}
          aria-label={`${taxonomy.shortName} guidance sections`}
          idPrefix={prefix}
          className="mb-3"
        />

        <TabPanel tabKey="taxonomy" idPrefix={prefix} active={activeTab === "taxonomy"}>
          <TaxonomyTable
            strategy={strategy}
            level={lvl}
            primary={emphasis?.primary ?? []}
            secondary={emphasis?.secondary ?? []}
          />
        </TabPanel>
        <TabPanel tabKey="writing" idPrefix={prefix} active={activeTab === "writing"}>
          <WritingGuidePanel strategy={strategy} />
        </TabPanel>
        <TabPanel tabKey="examples" idPrefix={prefix} active={activeTab === "examples"}>
          <ExamplesPanel testId={`${prefix}-examples-panel`} />
        </TabPanel>
      </div>
    </div>
  );
}
